import { useState } from "react";
import { Box, Group, Paper, Stack, Text } from "@mantine/core";
import { IconMapPin } from "@tabler/icons-react";
import CountryFlag, { countryHasRenderableFlag } from "@/components/shared/CountryFlag";
import CorridorDoubleArrow from "@/components/shared/CorridorDoubleArrow";
import corridorCardClasses from "@/components/ecosystem-head/EcosystemHeadCorridorCard.module.css";
import EcosystemExploreCardFooter from "@/pages/ecosystem-heads/components/EcosystemExploreCardFooter";
import {
  ECOSYSTEM_CORRIDOR_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_HEADER_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import type { ComingSoonEcosystemCorridor } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import { comingSoonCorridorHasFlags } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import classes from "./EcosystemCorridorPlaceholderCard.module.css";

const CARD_NAVY = "#04305c";
const CARD_BLUE = "#2563EB";
const CARD_BORDER = "#BFDBFE";

type EcosystemCorridorPlaceholderCardProps = {
  corridor: ComingSoonEcosystemCorridor;
  /** Fixed width for horizontal scroll layouts (e.g. explore page). */
  fixedWidth?: number;
  fullWidth?: boolean;
  /** Use compact flag badges (sector carousel) vs inline flags (explore grid). */
  variant?: "carousel" | "explore";
};

function HeaderFlag({ country }: { country: string }) {
  return (
    <Box
      px={10}
      py={8}
      style={{
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        lineHeight: 0,
      }}
    >
      <Box style={{ transform: "scale(1.25)", transformOrigin: "center" }}>
        <CountryFlag country={country} />
      </Box>
    </Box>
  );
}

function HeaderCountryName({ label }: { label: string }) {
  return (
    <Box
      px={12}
      py={8}
      style={{
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        border: `1px solid ${CARD_BORDER}`,
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        maxWidth: 120,
      }}
    >
      <Text size="xs" fw={700} c={CARD_NAVY} ta="center" lh={1.2}>
        {label}
      </Text>
    </Box>
  );
}

function renderPlaceholderHeader(corridor: ComingSoonEcosystemCorridor) {
  const originCountry = corridor.country?.trim() || null;
  const targetCountry = corridor.targetCountry?.trim() || null;
  const originLabel = corridor.originLabel.trim();
  const targetLabel = corridor.targetLabel.trim();

  if (originCountry && targetCountry) {
    const showFlags =
      comingSoonCorridorHasFlags(corridor) &&
      countryHasRenderableFlag(originCountry) &&
      countryHasRenderableFlag(targetCountry);

    if (showFlags) {
      return (
        <Group gap="sm" justify="center" wrap="nowrap" align="center">
          <HeaderFlag country={originCountry} />
          <CorridorDoubleArrow color={CARD_BLUE} />
          <HeaderFlag country={targetCountry} />
        </Group>
      );
    }
  }

  return (
    <Group gap="sm" justify="center" wrap="nowrap" align="center">
      <HeaderCountryName label={originLabel} />
      <CorridorDoubleArrow color={CARD_BLUE} />
      <HeaderCountryName label={targetLabel} />
    </Group>
  );
}

function renderPlaceholderLocationRow(corridor: ComingSoonEcosystemCorridor) {
  const originLabel = corridor.originLabel.trim();
  const targetLabel = corridor.targetLabel.trim();

  return (
    <Group gap={5} wrap="nowrap" align="center" justify="flex-start">
      <IconMapPin size={14} color={CARD_BLUE} style={{ flexShrink: 0 }} />
      <Group gap={4} wrap="nowrap" align="center">
        <Text size="xs" c={CARD_BLUE} fw={400}>
          {originLabel}
        </Text>
        <CorridorDoubleArrow size={14} color={CARD_BLUE} />
        <Text size="xs" c={CARD_BLUE} fw={400}>
          {targetLabel}
        </Text>
      </Group>
    </Group>
  );
}

export default function EcosystemCorridorPlaceholderCard({
  corridor,
  fixedWidth,
  fullWidth = false,
  variant = "carousel",
}: EcosystemCorridorPlaceholderCardProps) {
  const isUnderway = corridor.status === "underway";
  const buttonLabel = isUnderway ? "Underway" : "Coming Soon";
  const isExplore = variant === "explore";
  const cardHeight = isExplore ? ECOSYSTEM_EXPLORE_CARD_HEIGHT : ECOSYSTEM_CORRIDOR_CARD_HEIGHT;
  const cardWidth = fullWidth && isExplore ? undefined : isExplore ? ECOSYSTEM_EXPLORE_CARD_WIDTH : fixedWidth;
  const [isHovered, setIsHovered] = useState(false);
  const corridorTitle = corridor.corridorSubtitle.trim();

  return (
    <Paper
      radius="lg"
      shadow="none"
      h={cardHeight}
      className={corridorCardClasses.card}
      onMouseEnter={isExplore ? () => setIsHovered(true) : undefined}
      onMouseLeave={isExplore ? () => setIsHovered(false) : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${isExplore && isHovered ? "#93C5FD" : "#E2E8F0"}`,
        overflow: "hidden",
        boxShadow: isExplore
          ? isHovered
            ? "0 16px 40px rgba(4, 48, 92, 0.18)"
            : "0 8px 28px rgba(15, 23, 42, 0.08)"
          : "0 4px 20px rgba(15, 23, 42, 0.08)",
        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
        ...(cardWidth
          ? { flex: "0 0 auto", width: cardWidth, maxWidth: "85vw" }
          : fullWidth && isExplore
            ? { width: "100%" }
            : undefined),
      }}
    >
      <Box className={corridorCardClasses.cardContent}>
        <Box
          h={ECOSYSTEM_EXPLORE_CARD_HEADER_HEIGHT}
          px="md"
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            flexShrink: 0,
            paddingBottom: 2,
          }}
        >
          {renderPlaceholderHeader(corridor)}
        </Box>

        <Stack gap={8} px="md" pt={4} pb={4} style={{ flex: 1, minHeight: 0 }} align="stretch">
          <Stack gap={6} align="center">
            <Text
              fw={700}
              size="lg"
              c={CARD_NAVY}
              ta="center"
              lineClamp={2}
              style={{ lineHeight: 1.3, wordBreak: "break-word", width: "100%" }}
            >
              {corridorTitle}
            </Text>
            <Box className={corridorCardClasses.titleAccent} />
          </Stack>

          {renderPlaceholderLocationRow(corridor)}
        </Stack>

        <EcosystemExploreCardFooter>
          <Box className={`${classes.footerButton} ${corridorCardClasses.ctaComingSoon}`}>{buttonLabel}</Box>
        </EcosystemExploreCardFooter>
      </Box>
    </Paper>
  );
}
